import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, BookOpen, ExternalLink } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { assessmentSchema } from "@/lib/structuredData";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import niceGuyBook from "@/assets/no-more-mr-nice-guy.jpg";

const questions = [
  "I am likely to give to someone else before I give to myself.",
  "I apologize and say 'I'm sorry' when I think people might be upset with me.",
  "I try to fix other people's problems.",
  "I struggle to live up to my potential in work and career.",
  "I tend to feel resentful and take things personally.",
  "I have hidden thoughts or behaviors that I don't want people to know about.",
  "I hold back my feelings.",
  "I struggle to find my passion and purpose in life.",
  "I seek approval.",
  "I am less than completely honest.",
  "I am uncomfortable when people express strong feelings.",
  "Procrastination and/or finishing things I start is a problem for me.",
  "I feel frustrated with my sex life.",
  "I feel like I give more than I get.",
  "I have a tendency to settle.",
  "I try to follow the rules and do it right.",
  "I avoid conflict.",
  "I have difficulty approaching and talking to people I find attractive.",
  "I am more analytical than feeling.",
  "I tolerate things that feel bad to me.",
  "I have difficulty asking for help.",
  "I avoid situations and things that make me anxious."
];

const scaleLabels = ["Never", "Occasionally", "Often", "Always"];

const getResultText = (score: number) => {
  if (score >= 0 && score <= 21) {
    return {
      title: "You cheated.",
      description: "Or you're operating at an incredibly high level of self-awareness and authenticity. Either way, this score is rare.",
      color: "text-accent"
    };
  } else if (score >= 22 && score <= 33) {
    return {
      title: "You are near perfect or in denial.",
      description: "A very low score could indicate strong boundaries and authenticity—or a lack of honest self-reflection.",
      color: "text-primary"
    };
  } else if (score >= 34 && score <= 50) {
    return {
      title: "You have some Nice Guy tendencies.",
      description: "You're showing signs of people-pleasing patterns. Awareness is the first step toward change.",
      color: "text-highlight"
    };
  } else if (score >= 51 && score <= 70) {
    return {
      title: "You are definitely a Nice Guy!",
      description: "You're caught in the Nice Guy syndrome. The good news? You can break free with honest work and support.",
      color: "text-accent"
    };
  } else {
    return {
      title: "You could be a poster child for the Nice Guy syndrome!",
      description: "You're deeply entrenched in Nice Guy patterns. This is your wake-up call—and your invitation to real freedom.",
      color: "text-destructive"
    };
  }
};

const AMAZON_LINK = "https://amzn.to/4b8iIz6";

const NiceGuyAssessment = () => {
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [showResults, setShowResults] = useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);

  const handleAnswerChange = (questionIndex: number, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: parseInt(value)
    }));
  };

  const calculateScore = () => {
    return Object.values(answers).reduce((sum, val) => sum + val, 0);
  };

  const handleSubmit = () => {
    if (Object.keys(answers).length === questions.length) {
      setShowResults(true);
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleReset = () => {
    setAnswers({});
    setShowResults(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isComplete = Object.keys(answers).length === questions.length;
  const score = calculateScore();
  const result = getResultText(score);

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Nice Guy Assessment | Rob Lasa"
        description="Take this free assessment to see if you're stuck in Nice Guy patterns. Not a diagnosis—just a starting point for honest self-reflection."
        keywords="nice guy syndrome test, people pleasing assessment, self-reflection for men, authentic masculinity, nice guy patterns"
        url="/nice-guy-assessment"
        schema={assessmentSchema}
      />
      <Navigation />
      <Breadcrumbs />
      <div className="container mx-auto px-4 py-8 pt-8 max-w-4xl">

        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-foreground mb-4">
            No More Mr. Nice Guy Self-Assessment
          </h1>
          <p className="text-lg text-muted-foreground mb-4">
            Find out if you are a Nice Guy
          </p>
          <div className="flex items-start gap-2 text-sm text-muted-foreground bg-muted/30 p-4 rounded-lg">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>
              Assessment © 2012, Robert A. Glover, Ph.D.
            </p>
          </div>
        </div>

        {/* Results Section - Shows inline after submission */}
        {showResults && (
          <div ref={resultsRef} className="mb-8 space-y-6">
            <Card className="border-2 border-primary/30">
              <CardHeader>
                <CardTitle className="text-3xl font-serif">Your Results</CardTitle>
                <CardDescription className="text-lg">
                  Total Score: {score} out of 88
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className={`text-2xl font-bold ${result.color}`}>
                    {result.title}
                  </h3>
                  <p className="text-lg text-foreground/80 leading-relaxed">
                    {result.description}
                  </p>
                </div>

                {/* Book CTA */}
                <Card className="border-2 border-accent/30 bg-accent/5">
                  <CardContent className="pt-6">
                    <div className="flex flex-col sm:flex-row gap-6 items-center">
                      <img
                        src={niceGuyBook}
                        alt="No More Mr. Nice Guy by Dr. Robert Glover"
                        className="w-32 h-auto rounded-lg shadow-md flex-shrink-0"
                      />
                      <div className="space-y-3 text-center sm:text-left">
                        <h4 className="text-xl font-serif font-bold text-foreground">
                          Ready to break free?
                        </h4>
                        <p className="text-muted-foreground">
                          <em>No More Mr. Nice Guy</em> by Dr. Robert Glover is the book that started it all. 
                          If this assessment hit home, this is your next step.
                        </p>
                        <Button asChild size="lg" className="gap-2">
                          <a href={AMAZON_LINK} target="_blank" rel="noopener noreferrer">
                            <BookOpen className="w-4 h-4" />
                            Get the Book
                            <ExternalLink className="w-3 h-3" />
                            <span className="sr-only"> (opens in a new tab)</span>
                          </a>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Button onClick={handleReset} variant="outline" className="w-full">
                  Retake Assessment
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Assessment Questions</CardTitle>
            <CardDescription>
              Circle the answer that best applies: 1 = Never | 2 = Occasionally | 3 = Often | 4 = Always
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            {questions.map((question, index) => (
              <div key={index} className="space-y-3 pb-6 border-b last:border-b-0">
                <p className="font-medium text-foreground">
                  {index + 1}. {question}
                </p>
                <RadioGroup
                  value={answers[index]?.toString()}
                  onValueChange={(value) => handleAnswerChange(index, value)}
                  className="flex flex-wrap gap-4"
                >
                  {[1, 2, 3, 4].map((value) => (
                    <div key={value} className="flex items-center space-x-2">
                      <RadioGroupItem value={value.toString()} id={`q${index}-${value}`} />
                      <Label 
                        htmlFor={`q${index}-${value}`}
                        className="cursor-pointer font-normal"
                      >
                        {value} - {scaleLabels[value - 1]}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            ))}

            <div className="pt-4 flex gap-4">
              <Button
                onClick={handleSubmit}
                disabled={!isComplete}
                size="lg"
                className="w-full md:w-auto"
              >
                {isComplete ? "See My Results" : `Answer All Questions (${Object.keys(answers).length}/${questions.length})`}
              </Button>
              {Object.keys(answers).length > 0 && (
                <Button
                  onClick={handleReset}
                  variant="outline"
                  size="lg"
                >
                  Clear All
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>Assessment © 2012, Robert A. Glover, Ph.D.</p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default NiceGuyAssessment;
